import { useCallback, useMemo, useRef } from 'react';

import ReferralForm from 'components/modals/referral/form/ReferralForm';
import { referralFormValidation } from 'components/modals/referral/form/referralFormValidation';
import { ActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import StandardModal, { PatientName } from 'components/shared/StandardModal';
import { FormProvider } from 'context/form';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types';
import Upload from 'hooks/useDirectUpload/Upload';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { clientHasIntegration } from 'lib/clientIntegration';
import { simpleDashDate } from 'lib/date';
import { extractDigits } from 'lib/phone';
import Episode from 'models/Episode';
import Group from 'models/Group';
import GroupType from 'models/GroupType';
import LocationEpisode from 'models/LocationEpisode';
import { ReviewType } from 'models/Review';
import { createReview } from 'services/api/reviews';
import { useToastActions } from 'stores/toastStore';

import { buildReferralFormSteps, ReferralSteps, USState } from './form/helpers';

export type SelectValue = { label: string; value: string };
export const emptyValue: SelectValue = { label: '', value: '' };

export type ReferToFormValues = {
  note: ActivityInputValues;
  providerType: GroupType;
  group: Group;
  previousReviewId?: string;
  removedAttachmentIds?: string[];
  includeCcd?: boolean;
};

export type ReferralDeterminantsFormValues = {
  expectedDischargeDate?: Date;
  address1?: string;
  address2?: string;
  city?: string;
  state?: USState;
  zip?: string;
  phone?: string;
};

export type ReferralFormValues = Partial<ReferToFormValues & ReferralDeterminantsFormValues>;

export type ReferralFormMeta = {
  currentStep: ReferralSteps;
  isSubmitting: boolean;
  isUploadingAttachments: boolean;
  submitText: string;
  steps: ReferralSteps[];
  onCancel: () => void;
  onPreviousStep: (setMeta: (key: string, value: any) => void, currentStep: ReferralSteps) => void;
  onNextStep: (setMeta: (key: string, value: any) => void, currentStep: ReferralSteps) => void;
};

type SendReferralModalProps = {
  onCancel: () => void;
  onSuccess: () => void;
  locationEpisode: LocationEpisode;
  patientName: string;
  episode: Episode;
};

export default function SendReferralModal(props: SendReferralModalProps) {
  const { onCancel, onSuccess, patientName, locationEpisode, episode } = props;
  const { addToast } = useToastActions();
  const isSubmittingRef = useRef(false);

  const invalidateQueries = useInvalidateQueries();

  const { mutate } = useMutateWithToast({
    mutationFn: createReview,
    onSuccess: () => {
      invalidateQueries({
        activities: true,
        locationEpisode: { id: locationEpisode.id },
        referrals: true,
        attachmentsCount: true,
      });
      onSuccess();
    },
  });

  const latestReferralData = locationEpisode.latestReferralReview?.data;
  const projectedDischargeDate = locationEpisode.projectedDischargeReview?.date;
  const importedPatientValues = useMemo(
    () => ({ address: episode.patient.address, phoneNumber: episode.patient.phoneNumber }),
    [episode.patient.address, episode.patient.phoneNumber]
  );

  const canAutoIncludeCcd =
    clientHasIntegration(locationEpisode.owner.client) && locationEpisode.patient.hasIntegrationExternalId;

  // Mirror the re-send predicate used in ReferToStep — when the previous
  // referral was rejected/canceled, its CCD carries over via the existing
  // attachment flow, so we don't want a fresh ccd.generate event. Starting
  // includeCcd at false (instead of true) prevents the form from
  // submitting a stale truthy default that the user can't toggle off
  // (the checkbox is also hidden in this case).
  const latestReferral = locationEpisode.latestReferralReview;
  const shouldCarryOverAttachments = !!latestReferral && (latestReferral.rejected || latestReferral.canceled);

  const initialValues = useMemo(() => {
    return {
      expectedDischargeDate: projectedDischargeDate ?? undefined,
      address1: latestReferralData?.address1 ?? importedPatientValues?.address?.address1,
      address2: latestReferralData?.address2 ?? importedPatientValues?.address?.address2,
      city: latestReferralData?.city ?? importedPatientValues?.address?.city,
      state: latestReferralData?.state ?? importedPatientValues?.address?.state,
      zip: (latestReferralData?.zip ?? importedPatientValues?.address?.zip)?.toString().slice(0, 5) ?? '',
      phone: '',
      includeCcd: canAutoIncludeCcd && !shouldCarryOverAttachments,
    };
  }, [
    latestReferralData,
    importedPatientValues,
    projectedDischargeDate,
    canAutoIncludeCcd,
    shouldCarryOverAttachments,
  ]);

  const handleSubmit = async (
    values: FormValues<ReferralFormValues>,
    meta: FormMeta<ReferralFormMeta>,
    helpers: FormHelpers<ReferralFormValues, ReferralFormMeta>
  ) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    helpers.setMeta('isSubmitting', true);

    delete values.note?.escalationType;

    // Filter out carried-over attachments from note - API will handle copying them
    const newUploads = values.note?.attachments?.filter((item) => item instanceof Upload) ?? [];

    mutate(
      {
        locationEpisodeId: locationEpisode.id,
        groupId: values.group?.id ?? '',
        type: ReviewType.REFERRAL,
        note: values.note
          ? {
              ...values.note,
              attachments: newUploads.map((item) => (item as Upload).serialize()),
            }
          : undefined,
        previousReviewId: values.previousReviewId,
        removedAttachmentIds: values.removedAttachmentIds,
        expectedDischargeDate: simpleDashDate(values.expectedDischargeDate),
        address1: values.address1,
        address2: values.address2,
        city: values.city,
        state: values.state,
        zip: values.zip,
        phoneNumber: extractDigits(values.phone || '') || undefined,
        includeCcd: canAutoIncludeCcd ? !!values.includeCcd : undefined,
      },
      {
        onError: () => {
          addToast({ text: 'Something went wrong. Please try again' });
          isSubmittingRef.current = false;
        },
        onSettled: () => {
          helpers.setMeta('isSubmitting', false);
          isSubmittingRef.current = false;
        },
      }
    );
  };

  const steps = useMemo(() => buildReferralFormSteps(), []);

  const onPreviousStep = useCallback(
    (setMeta: (key: string, value: any) => void, currentStep: ReferralSteps) => {
      if (currentStep === ReferralSteps.ReferTo) {
        onCancel();
        return;
      }

      const currentIndex = steps.indexOf(currentStep);
      setMeta('currentStep', steps[currentIndex - 1]);
    },
    [steps, onCancel]
  );

  const onNextStep = useCallback(
    (setMeta: (key: string, value: any) => void, currentStep: ReferralSteps) => {
      const currentIndex = steps.indexOf(currentStep);

      setMeta('currentStep', steps[currentIndex + 1]);
    },
    [steps]
  );

  return (
    <StandardModal title='Refer Patient' onCancel={onCancel}>
      <PatientName>Patient: {patientName}</PatientName>
      <FormProvider<ReferralFormValues, ReferralFormMeta>
        onSubmit={handleSubmit}
        yupSchema={referralFormValidation()}
        initialValues={initialValues as any}
        initialMeta={{
          currentStep: ReferralSteps.ReferTo,
          steps,
          submitText: 'Submit Referral',
          onCancel,
          onNextStep,
          onPreviousStep,
        }}
        resetOnInitialValuesChange>
        <ReferralForm
          locationEpisode={locationEpisode}
          initialPhoneNumber={latestReferralData?.phoneNumber ?? importedPatientValues?.phoneNumber}
        />
      </FormProvider>
    </StandardModal>
  );
}
